import { cva, type VariantProps } from 'class-variance-authority';
import type { FC, ReactNode } from 'react';
import { cn } from '../lib/utils';

export const chatActionCardDotVariants = cva('size-[7px] shrink-0 rounded-full', {
  variants: {
    tone: {
      critical: 'bg-[var(--z-danger)]',
      warning: 'bg-warning',
      healthy: 'bg-success',
      neutral: 'bg-muted-foreground'
    }
  },
  defaultVariants: { tone: 'neutral' }
});

interface ChatActionCardProps extends VariantProps<typeof chatActionCardDotVariants> {
  /** Where it comes from, e.g. `EDR · Courier`. */
  meta: ReactNode;
  title: ReactNode;
  children: ReactNode;
  /** Buttons along the bottom. Use `size="xs"`. */
  actions?: ReactNode;
  /** Spoken before the meta so the dot's meaning is not colour-only. */
  toneLabel?: string;
  className?: string;
}

/** A recommended next step: status dot, source, what to do and why. */
export const ChatActionCard: FC<ChatActionCardProps> = ({
  tone,
  toneLabel,
  meta,
  title,
  children,
  actions,
  className
}) => (
  <article
    className={cn(
      'flex flex-col gap-2 rounded-xl border border-[var(--z-border-subtle)] bg-[image:var(--z-glass-card)] px-4 pt-4 pb-3.5',
      className
    )}
  >
    <p className="flex items-center gap-2 text-caption text-muted-foreground">
      <span aria-hidden="true" className={chatActionCardDotVariants({ tone })} />
      {toneLabel ? <span className="sr-only">{toneLabel}:</span> : null}
      {meta}
    </p>
    <h4 className="text-body-lg leading-5 font-semibold text-foreground">{title}</h4>
    <div className="text-body leading-[1.1875rem] text-pretty text-muted-foreground">
      {children}
    </div>
    {actions ? <div className="mt-1 flex flex-wrap gap-2">{actions}</div> : null}
  </article>
);
